import { Component, signal, computed, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { toast } from 'ngx-sonner';
import { ButtonComponent } from '../../../../../../shared/components/ui/button/button.component';
import { IconComponentV2 } from '../../../../../../shared/components/icon-component/icon.component';
import { ToggleComponent } from '../../../../../../shared/components/ui/toggle/toggle.component';
import {
  DropdownComponent,
  type DropdownOption,
} from '../../../../../../shared/components/ui/dropdown/dropdown.component';
import { MicroServiceHttpService } from '../../../../../../core/services/microServiceHttp.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PromptGeneratorModalComponent } from './prompt-generator-modal/prompt-generator-modal.component';
import { DefaultPromptAPIData, modelAPIData, providerAPIData } from '../../../default-settings/default-settings.component';

const DEFAULT_PROMPT_NONE = 'none';

const SETTINGS_SECTION_IDS = [
  'temperature',
  'provider',
  'model',
  'maxTokens',
  'budget',
  'timeout',
  'stream',
] as const;

type AgentDraftSnapshot = {
  systemPrompt: string;
  firstMessage: string | null;
  temperature: number;
  providerId: number | null;
  llmProvider: string;
  llmModel: string;
  modelId: number | null;
  maxTokens: number;
  weeklyTokensBudget: number;
  llmTimeout: number;
  outputStream: boolean;
  defaultPromptId: number | null;
};

/** GET/PATCH chatbot/bot/:id/settings — fields used when mapping into the draft. */
interface AgentSettingsResponse {
  systemPrompt?: string;
  firstMessage?: string | null;
  defaultPromptId?: number | null;
  temperature: number;
  modelId?: number;
  maxTokens?: number | string;
  weeklyTokenBudget?: number | string;
  aiTimeoutMs: number;
  enableStream: boolean;
  aiModel?: {
    pid: number;
    providerId: number;
    model: string;
    providerName: string;
  };
}

/** PATCH chatbot/bot/:id/settings — request body (full replacement of these fields). */
interface AgentSettingsUpdatePayload {
  systemPrompt: string;
  firstMessage: string | null;
  defaultPromptId: number | null;
  temperature: number;
  providerId: number;
  modelId: number;
  maxTokens: number;
  weeklyTokenBudget: number;
  aiTimeoutMs: number;
  enableStream: boolean;
}

@Component({
  selector: 'app-agent',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    IconComponentV2,
    ToggleComponent,
    DropdownComponent,
    MatDialogModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './agent.component.html'
})
export class AgentComponent implements OnInit {

  botId = input<number | null>(null);

  private microServiceHttpService = inject(MicroServiceHttpService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);

  private settingsLoaded = signal(false);

  agentTempData = signal<AgentDraftSnapshot>({
    systemPrompt: '',
    firstMessage: null,
    temperature: 0.7,
    providerId: null,
    llmProvider: '',
    llmModel: '',
    modelId: null,
    maxTokens: 4096,
    weeklyTokensBudget: 500000,
    llmTimeout: 30,
    outputStream: true,
    defaultPromptId: null,
  });

  baselineDraft = signal<AgentDraftSnapshot | null>(null);
  private baselineCaptured = signal(false);
  saveDraftLoading = signal(false);

  defaultPrompts = signal<DefaultPromptAPIData[]>([]);
  promptsLoading = signal(false);

  providers = signal<providerAPIData[]>([]);
  providerModels = signal<Record<number, modelAPIData[]>>({});
  modelsLoading = signal<number | null>(null);

  openSections = signal<Set<string>>(new Set(['temperature']));

  /** True when every accordion in the Settings panel is closed. */
  settingsPanelAllCollapsed = computed(() => {
    const open = this.openSections();
    return SETTINGS_SECTION_IDS.every((id) => !open.has(id));
  });

  /** Icon: expand when nothing open, collapse when any section is open. */
  settingsExpandCollapseIcon = computed(() =>
    this.settingsPanelAllCollapsed() ? 'expand' : 'shrink',
  );

  providerLabel = computed(() => {
    const d = this.agentTempData();
    const p = this.providers().find((x) => x.pid === d.providerId);
    return p?.name ?? d.llmProvider ?? '—';
  });

  modelsForSelectedProvider = computed(() => {
    const pid = this.agentTempData().providerId;
    if (pid == null) return [];
    return this.providerModels()[pid] ?? [];
  });

  modelDropdownOptions = computed((): DropdownOption[] =>
    this.modelsForSelectedProvider().map((m) => ({
      value: String(m.pid),
      label: m.name || m.model,
    })),
  );

  modelDropdownSelectedValue = computed(() => {
    const id = this.agentTempData().modelId;
    return id != null ? String(id) : '';
  });

  activeDefaultPrompts = computed(() =>
    this.defaultPrompts().filter((p) => !p.deletedAt),
  );

  defaultPromptDropdownOptions = computed((): DropdownOption[] => {
    const rows: DropdownOption[] = [
      {
        value: DEFAULT_PROMPT_NONE,
        label: 'No default prompt',
        description: 'Only your system prompt below will apply.',
        tooltip: 'Only your system prompt below will apply.',
      },
    ];
    for (const p of this.activeDefaultPrompts()) {
      const title = (p.label || '').trim() || `Prompt #${p.pid}`;
      const desc =
        p.prompt && p.prompt.length > 0
          ? p.prompt.slice(0, 120) + (p.prompt.length > 120 ? '…' : '')
          : undefined;
      rows.push({
        value: String(p.pid),
        label: title,
        description: desc,
        tooltip: p.prompt,
      });
    }
    return rows;
  });

  defaultPromptDropdownSelectedValue = computed(() => {
    const id = this.agentTempData().defaultPromptId;
    if (id == null) return DEFAULT_PROMPT_NONE;
    const ok = this.activeDefaultPrompts().some((p) => p.pid === id);
    return ok ? String(id) : DEFAULT_PROMPT_NONE;
  });

  selectedDefaultPromptPreview = computed(() => {
    const id = this.agentTempData().defaultPromptId;
    if (id == null) return null;
    return this.activeDefaultPrompts().find((p) => p.pid === id) ?? null;
  });

  defaultPromptPreviewExpanded = signal(false);

  /** Show View more/less when prompt exceeds a short preview. */
  defaultPromptPreviewShowToggle = computed(() => {
    const text = this.selectedDefaultPromptPreview()?.prompt;
    if (!text) return false;
    if (text.length > 120) return true;
    return text.split('\n').length > 4;
  });

  defaultPromptDropdownSearchable = computed(
    () => this.activeDefaultPrompts().length > 5,
  );

  hasDraftChanges = computed(() => {
    const base = this.baselineDraft();
    if (!base) return false;
    return JSON.stringify(base) !== JSON.stringify(this.agentTempData());
  });

  temperatureLabel = computed(() => {
    const t = this.agentTempData().temperature;
    if (t <= 0.3) return 'Precise';
    if (t <= 0.7) return 'Balanced';
    return 'Creative';
  });

  formattedBudget = computed(() => {
    const v = this.agentTempData().weeklyTokensBudget;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
    return v.toString();
  });

  patchAgentTemp(partial: Partial<AgentDraftSnapshot>): void {
    this.agentTempData.update((curr) => ({ ...curr, ...partial }));
  }

  private snapshotDraft(): AgentDraftSnapshot {
    return { ...this.agentTempData() };
  }

  private toNum(value: unknown, fallback: number): number {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    if (typeof value === 'string') {
      const n = Number(value);
      return Number.isNaN(n) ? fallback : n;
    }
    return fallback;
  }

  /**
   * Maps GET/PATCH response into the in-memory draft and syncs default prompt + model labels.
   * Caller should set `baselineCaptured` to false before this when re-baselining after PATCH.
   */
  private applyFetchedAgentSettings(data: AgentSettingsResponse): void {
    const aiMs = data.aiTimeoutMs;
    const timeoutSec =
      aiMs >= 1000 ? Math.round(aiMs / 1000) : Math.round(aiMs);
    const ai = data.aiModel;
    const resolvedModelId = data.modelId ?? ai?.pid ?? null;
    const providerId = ai?.providerId ?? null;
    const rawDefaultPromptId = data.defaultPromptId;
    this.agentTempData.set({
      systemPrompt: data.systemPrompt ?? '',
      firstMessage: data.firstMessage ?? null,
      temperature: data.temperature,
      providerId,
      llmProvider: ai?.providerName ?? '',
      llmModel: ai?.model ?? '',
      modelId: resolvedModelId,
      maxTokens: this.toNum(data.maxTokens, 4096),
      weeklyTokensBudget: this.toNum(data.weeklyTokenBudget, 0),
      llmTimeout: timeoutSec,
      outputStream: data.enableStream,
      defaultPromptId:
        rawDefaultPromptId !== undefined && rawDefaultPromptId !== null
          ? rawDefaultPromptId
          : null,
    });
    this.syncDefaultPromptIdWithList();
    if (providerId != null) {
      this.getProviderModelsApi(providerId, {
        afterLoad: (models) => {
          const mid = this.agentTempData().modelId;
          const match = mid != null ? models.find((m) => m.pid === mid) : undefined;
          if (match) {
            this.patchAgentTemp({
              llmModel: match.model,
              modelId: match.pid,
            });
          } else if (models.length > 0) {
            this.patchAgentTemp({
              llmModel: models[0].model,
              modelId: models[0].pid,
            });
          }
          this.tryCaptureBaselineDraft();
        },
      });
    }
    this.tryCaptureBaselineDraft();
  }

  private buildUpdatePayload(
    draft: AgentDraftSnapshot,
  ): AgentSettingsUpdatePayload | null {
    const systemPrompt = draft.systemPrompt.trim();
    if (!systemPrompt) {
      toast.error('System prompt is required');
      return null;
    }
    if (draft.providerId == null || draft.modelId == null) {
      toast.error('Please select an LLM provider and model');
      return null;
    }
    const t = draft.temperature;
    if (t < 0 || t > 2) {
      toast.error('Temperature must be between 0 and 2');
      return null;
    }
    const maxTokens = Math.floor(draft.maxTokens);
    if (!Number.isFinite(maxTokens) || maxTokens < 1) {
      toast.error('Max tokens must be an integer ≥ 1');
      return null;
    }
    const weeklyTokenBudget = Math.floor(draft.weeklyTokensBudget);
    if (!Number.isFinite(weeklyTokenBudget) || weeklyTokenBudget < 0) {
      toast.error('Weekly token budget must be an integer ≥ 0');
      return null;
    }
    const aiTimeoutMs = Math.round(draft.llmTimeout * 1000);
    if (!Number.isFinite(aiTimeoutMs) || aiTimeoutMs < 1) {
      toast.error('LLM timeout must be at least 1 ms');
      return null;
    }

    let firstMessage: string | null = draft.firstMessage;
    if (firstMessage !== null && firstMessage.trim() === '') {
      firstMessage = null;
    }

    return {
      systemPrompt,
      firstMessage,
      defaultPromptId: draft.defaultPromptId,
      temperature: t,
      providerId: draft.providerId,
      modelId: draft.modelId,
      maxTokens,
      weeklyTokenBudget,
      aiTimeoutMs,
      enableStream: draft.outputStream,
    };
  }

  private tryCaptureBaselineDraft(): void {
    if (this.baselineCaptured()) return;
    if (!this.settingsLoaded()) return;
    if (this.promptsLoading()) return;
    const pid = this.agentTempData().providerId;
    if (pid != null) {
      if (this.modelsLoading() === pid) return;
      if (this.providerModels()[pid] === undefined) return;
    }
    this.baselineDraft.set(this.snapshotDraft());
    this.baselineCaptured.set(true);
  }

  onSaveDraft(): void {
    if (!this.baselineDraft()) {
      toast.warning('Settings are still loading. Please wait before saving.');
      return;
    }
    if (!this.hasDraftChanges()) {
      toast.warning('No value changes found', {
        description: 'Update at least one field before saving.',
      });
      return;
    }
    const bid = this.botId();
    if (bid == null) return;
    const body = this.buildUpdatePayload(this.snapshotDraft());
    if (!body) return;

    const url = `chatbot/bot/${bid}/settings`;
    this.saveDraftLoading.set(true);
    this.microServiceHttpService
      .patch<{ data: AgentSettingsResponse }>('whitelabel', url, body, {
        showToast: true,
        successMessage: 'Agent settings saved successfully',
      })
      .pipe(finalize(() => this.saveDraftLoading.set(false)))
      .subscribe({
        next: (response: { data?: AgentSettingsResponse } | AgentSettingsResponse) => {
          const raw = response as { data?: AgentSettingsResponse };
          const data = raw?.data ?? (response as AgentSettingsResponse);
          if (!data || typeof data !== 'object') return;
          this.baselineCaptured.set(false);
          this.applyFetchedAgentSettings(data);
        },
      });
  }

  openPromptGenerator() {
    const bid = this.botId();
    if (!bid) return;

    this.dialog.open(PromptGeneratorModalComponent, {
      data: { chatbotId: bid },
      width: '600px',
      maxWidth: '95vw',
      panelClass: 'modern-modal-panel'
    }).afterClosed().subscribe((newPrompt: string) => {
      if (newPrompt) {
        this.patchAgentTemp({ systemPrompt: newPrompt });
        toast.success('System prompt updated', {
          description: 'The AI-generated prompt has been applied to your agent draft.'
        });
      }
    });
  }

  toggleSection(section: string): void {
    this.openSections.update(set => {
      const next = new Set(set);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
    if (this.isSectionOpen(section) && section === 'model') {
      const pid = this.agentTempData().providerId;
      if (pid != null && this.providerModels()[pid] === undefined) {
        this.getProviderModelsApi(pid);
      }
    }
  }

  isSectionOpen(section: string): boolean {
    return this.openSections().has(section);
  }

  toggleExpandCollapseAllSettings(): void {
    if (this.settingsPanelAllCollapsed()) {
      this.openSections.set(new Set(SETTINGS_SECTION_IDS));
      const pid = this.agentTempData().providerId;
      if (pid != null && this.providerModels()[pid] === undefined) {
        this.getProviderModelsApi(pid);
      }
    } else {
      this.openSections.set(new Set());
    }
  }

  onProviderSelect(providerId: number): void {
    const p = this.providers().find((x) => x.pid === providerId);
    this.patchAgentTemp({
      providerId,
      llmProvider: p?.name ?? '',
    });
    this.getProviderModelsApi(providerId, {
      afterLoad: (models) => {
        if (models.length === 0) {
          this.patchAgentTemp({ llmModel: '', modelId: null });
          return;
        }
        this.patchAgentTemp({
          llmModel: models[0].model,
          modelId: models[0].pid,
        });
      },
    });
  }

  onModelSelect(modelId: number | string | null): void {
    if (modelId == null || modelId === '') return;
    const id = typeof modelId === 'string' ? Number(modelId) : modelId;
    if (Number.isNaN(id)) return;
    const pid = this.agentTempData().providerId;
    if (pid == null) return;
    const models = this.providerModels()[pid] ?? [];
    const m = models.find((x) => x.pid === id);
    if (!m) return;
    this.patchAgentTemp({ modelId: m.pid, llmModel: m.model });
  }

  onModelDropdownChange(value: string | string[] | null): void {
    const v = Array.isArray(value) ? value[0] : value;
    this.onModelSelect(v ?? null);
  }

  onDefaultPromptDropdownChange(value: string | string[] | null): void {
    const v = Array.isArray(value) ? value[0] : value;
    this.defaultPromptPreviewExpanded.set(false);
    if (v == null || v === '' || v === DEFAULT_PROMPT_NONE) {
      this.patchAgentTemp({ defaultPromptId: null });
      return;
    }
    const id = Number(v);
    if (Number.isNaN(id)) return;
    this.patchAgentTemp({ defaultPromptId: id });
  }

  toggleDefaultPromptPreview(): void {
    this.defaultPromptPreviewExpanded.update((x) => !x);
  }

  private syncDefaultPromptIdWithList(): void {
    const id = this.agentTempData().defaultPromptId;
    if (id == null) return;
    const active = this.defaultPrompts().filter((p) => !p.deletedAt);
    if (active.length === 0) return;
    if (!active.some((p) => p.pid === id)) {
      this.patchAgentTemp({ defaultPromptId: null });
    }
  }

  getDefaultPromptsApi(): void {
    this.promptsLoading.set(true);
    this.microServiceHttpService.get('whitelabel', 'default-config/default-prompts', {
      showToast: false,
    }).subscribe({
      next: (response: any) => {
        const list = (response.data ?? []) as DefaultPromptAPIData[];
        this.defaultPrompts.set(list);
        this.promptsLoading.set(false);
        this.syncDefaultPromptIdWithList();
        this.tryCaptureBaselineDraft();
      },
      error: (err: unknown) => {
        console.error(err);
        this.promptsLoading.set(false);
      },
    });
  }

  getProvidersApi(): void {
    this.microServiceHttpService.get('whitelabel', 'default-config/llm-providers', {
      showToast: false,
    }).subscribe({
      next: (response: any) => {
        this.providers.set(response.data ?? []);
      },
      error: (err: unknown) => {
        console.error(err);
      },
    });
  }

  getProviderModelsApi(
    providerId: number,
    opts?: { afterLoad?: (models: modelAPIData[]) => void },
  ): void {
    const cached = this.providerModels()[providerId];
    if (cached !== undefined) {
      opts?.afterLoad?.(cached);
      return;
    }
    this.modelsLoading.set(providerId);
    this.microServiceHttpService.get('whitelabel', `default-config/llm-models/${providerId}`, {
      showToast: false,
      showLoader: false,
    }).subscribe({
      next: (response: any) => {
        const list = (response.data ?? []) as modelAPIData[];
        this.providerModels.update((map) => ({ ...map, [providerId]: list }));
        this.modelsLoading.set(null);
        opts?.afterLoad?.(list);
      },
      error: (err: unknown) => {
        console.error(err);
        this.modelsLoading.set(null);
      },
    });
  }

  onStreamToggle(isChecked: boolean): void {
    this.patchAgentTemp({ outputStream: isChecked });
  }

  ngOnInit(): void {
    if (this.botId()) {
      this.getDefaultPromptsApi();
      this.getProvidersApi();
      this.getAgentSettingsApi(this.botId()!);
    } else {
      this.router.navigate(['../list'], {
        relativeTo: this.route,
      });
    }
  }

  getAgentSettingsApi(chatbotId: number): void {
    this.baselineCaptured.set(false);
    this.baselineDraft.set(null);
    this.settingsLoaded.set(false);

    const url = `chatbot/bot/${chatbotId}/settings`;

    this.microServiceHttpService.get('whitelabel', url, {
      showToast: false,
      successMessage: 'Agent settings fetched successfully',
    }).subscribe({
      next: (response: any) => {
        const data = response.data as AgentSettingsResponse;
        this.settingsLoaded.set(true);
        this.applyFetchedAgentSettings(data);
      },
      error: (_: any) => {
        this.router.navigate(['../list'], {
          relativeTo: this.route,
        });
      }
    })
  }
}
